import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { MapPin, X, Check, ChevronDown, Search } from "lucide-react";
import { useApp } from "@/context";
import http from "@/api";

/**
 * Big, prominent country + city/branch selector for the home page.
 * Talabat-style: flag emoji + country + city. Tapping opens a bottom sheet
 * to switch country, then city (auto-loads /branches?country=).
 *
 * Persists to localStorage via context (`country`, `city`).
 */
export default function LocationPicker() {
  const { lang, country, setCountry, city, setCity, countries, branchId, setBranchId } = useApp();
  const [open, setOpen] = useState(false);

  // فتح المحدد برمجياً (مثلاً عند اختيار السعودية من الهيدر) + تلقائياً لعميل
  // سعودي لم يختر فرعه بعد — مرة واحدة بالجلسة
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("rg:open-location", h);
    return () => window.removeEventListener("rg:open-location", h);
  }, []);
  useEffect(() => {
    if (country === "SA" && !branchId && !sessionStorage.getItem("rg_sa_prompted")) {
      sessionStorage.setItem("rg_sa_prompted", "1");
      setOpen(true);
    }
  }, [country, branchId]);

  const current = countries.find((c) => c.code === country) || {
    code: country, name_ar: country, name_en: country, flag: "🌐",
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
              data-testid="location-picker-btn"
              className="w-full card-soft px-3 py-2 flex items-center gap-2.5 text-start hover:shadow-md transition-all"
              style={{ borderLeft: "3px solid var(--secondary)" }}>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xl shrink-0"
             style={{ background: "var(--bg-subtle)" }}>
          {current.flag || "🌐"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-widest opacity-60">
            {lang === "ar" ? "التوصيل إلى" : "Deliver to"}
          </div>
          <div className="font-bold text-[13px] leading-tight truncate">
            {lang === "ar" ? current.name_ar : current.name_en}
            {city
              ? <span className="opacity-70 font-normal"> · {city}</span>
              : <span className="opacity-55 font-normal text-[11px]"> · {lang === "ar" ? "اختر مدينتك / فرعك" : "Choose city / branch"}</span>}
          </div>
        </div>
        <ChevronDown size={16} className="opacity-50 shrink-0" />
      </button>

      {open && <LocationSheet onClose={() => setOpen(false)}
                              countries={countries}
                              country={country} setCountry={setCountry}
                              city={city} setCity={setCity} lang={lang}
                              setBranchId={setBranchId} />}
    </>
  );
}

function LocationSheet({ onClose, countries, country, setCountry, city, setCity, lang, setBranchId }) {
  const [tab, setTab] = useState("country");  // country | city
  const [search, setSearch] = useState("");
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [branchSel, setBranchSel] = useState(null); // فرع إقليمي اختير — اختر المدينة داخله

  // Load branches whenever country changes (or sheet opens with current country)
  useEffect(() => {
    let cancel = false;
    setLoadingBranches(true);
    http.get(`/branches?country=${country}`)
      .then(({ data }) => { if (!cancel) setBranches(data || []); })
      .catch(() => { if (!cancel) setBranches([]); })
      .finally(() => { if (!cancel) setLoadingBranches(false); });
    return () => { cancel = true; };
  }, [country]);

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((c) =>
      (c.name_ar || "").toLowerCase().includes(q) ||
      (c.name_en || "").toLowerCase().includes(q) ||
      (c.code || "").toLowerCase().includes(q)
    );
  }, [countries, search]);

  // Cities are loaded from branches (each branch has city/name) + a global default list
  const filteredCities = useMemo(() => {
    const q = search.trim().toLowerCase();
    const items = [];
    const seen = new Set();
    branches.forEach((b) => {
      const key = `${b.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      items.push({
        id: b.id,
        name_ar: b.city_ar || b.name_ar || b.city || "",
        name_en: b.city_en || b.name_en || b.city || "",
        branch_name_ar: b.name_ar,
        branch_name_en: b.name_en,
        coverage_ar: b.coverage_cities_ar || [],
        coverage_en: b.coverage_cities_en || [],
        country_code: b.country_code,
      });
    });
    if (!q) return items;
    return items.filter((c) =>
      (c.name_ar || "").toLowerCase().includes(q) ||
      (c.name_en || "").toLowerCase().includes(q) ||
      (c.branch_name_ar || "").toLowerCase().includes(q) ||
      (c.branch_name_en || "").toLowerCase().includes(q) ||
      (c.coverage_ar || []).some((x) => x.toLowerCase().includes(q)) ||
      (c.coverage_en || []).some((x) => x.toLowerCase().includes(q))
    );
  }, [branches, search]);

  const pickCountry = (c) => {
    setCountry(c.code);
    setCity("");  // reset city when country changes
    setBranchId("");
    setBranchSel(null);
    setSearch("");
    setTab("city");
  };
  const pickCity = (b) => {
    const cov = lang === "ar" ? (b.coverage_ar || []) : (b.coverage_en || []);
    if (cov.length) { setBranchSel(b); return; } // فرع إقليمي — اختر مدينتك داخله
    setCity(lang === "ar" ? (b.name_ar || b.branch_name_ar) : (b.name_en || b.branch_name_en));
    setBranchId(b.id || "");
    onClose();
  };
  const pickCoverageCity = (name) => {
    setCity(name);
    setBranchId(branchSel?.id || "");
    setBranchSel(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
         data-testid="location-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <motion.div
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 28, stiffness: 280 }}
        className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2 font-bold">
            <MapPin size={18} />
            {lang === "ar" ? "اختر موقع التوصيل" : "Choose delivery location"}
          </div>
          <button onClick={onClose} data-testid="location-sheet-close"
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/5">
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex bg-black/5 m-3 rounded-lg p-0.5 text-sm">
          <button onClick={() => setTab("country")}
                  data-testid="loc-tab-country"
                  className={`flex-1 px-3 py-1.5 rounded-md transition-colors ${tab === "country" ? "bg-white shadow-sm font-semibold" : "opacity-70"}`}>
            🌍 {lang === "ar" ? "البلد" : "Country"}
          </button>
          <button onClick={() => setTab("city")}
                  data-testid="loc-tab-city"
                  className={`flex-1 px-3 py-1.5 rounded-md transition-colors ${tab === "city" ? "bg-white shadow-sm font-semibold" : "opacity-70"}`}>
            🏙️ {lang === "ar" ? "المدينة / الفرع" : "City / Branch"}
          </button>
        </div>

        {/* Search */}
        <div className="px-3 mb-2">
          <div className="relative">
            <Search size={16} className="absolute top-1/2 -translate-y-1/2 start-3 opacity-40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   data-testid="loc-search"
                   placeholder={lang === "ar" ? "ابحث..." : "Search..."}
                   className="w-full ps-9 pe-3 py-2 rounded-lg bg-black/5 text-sm focus:outline-none focus:bg-black/10" />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 pb-3" data-testid="loc-list">
          {tab === "country" && filteredCountries.map((c) => {
            const active = c.code === country;
            return (
              <button key={c.code} onClick={() => pickCountry(c)}
                      data-testid={`loc-country-${c.code}`}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-black/[0.04] transition-colors text-start">
                <div className="text-3xl">{c.flag || "🌐"}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">
                    {lang === "ar" ? c.name_ar : c.name_en}
                  </div>
                  <div className="text-[11px] opacity-60">
                    {c.currency_code || ""} {c.code ? `· ${c.code}` : ""}
                  </div>
                </div>
                {active && <Check size={18} className="text-emerald-600" />}
              </button>
            );
          })}

          {tab === "city" && branchSel && (
            <div data-testid="coverage-city-step">
              <button onClick={() => setBranchSel(null)}
                      className="w-full flex items-center gap-2 p-2 text-sm opacity-70 hover:opacity-100"
                      data-testid="coverage-back-btn">
                ← {lang === "ar" ? "رجوع للفروع" : "Back to branches"}
              </button>
              <div className="px-3 py-2 text-sm font-bold">
                {lang === "ar" ? branchSel.branch_name_ar : branchSel.branch_name_en}
                <span className="block text-[11px] font-normal opacity-60 mt-0.5">
                  {lang === "ar" ? "اختر مدينتك" : "Choose your city"}
                </span>
              </div>
              {(lang === "ar" ? branchSel.coverage_ar : branchSel.coverage_en).map((name) => (
                <button key={name} onClick={() => pickCoverageCity(name)}
                        data-testid={`coverage-city-${name}`}
                        className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-black/[0.04] transition-colors text-start">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                       style={{ background: "var(--bg-subtle)" }}>📍</div>
                  <div className="font-semibold text-sm flex-1">{name}</div>
                  {city === name && <Check size={18} className="text-emerald-600" />}
                </button>
              ))}
            </div>
          )}

          {tab === "city" && !branchSel && (
            loadingBranches ? (
              <div className="text-center text-sm opacity-60 p-6">...</div>
            ) : filteredCities.length === 0 ? (
              <div className="text-center text-sm opacity-60 p-6">
                {lang === "ar" ? "لا توجد فروع لهذه الدولة بعد" : "No branches for this country yet"}
              </div>
            ) : filteredCities.map((c) => {
              const label = lang === "ar" ? (c.name_ar || c.branch_name_ar) : (c.name_en || c.branch_name_en);
              const active = label === city;
              const cov = lang === "ar" ? (c.coverage_ar || []) : (c.coverage_en || []);
              return (
                <button key={c.id} onClick={() => pickCity(c)}
                        data-testid={`loc-city-${c.id}`}
                        className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-black/[0.04] transition-colors text-start">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                       style={{ background: "var(--bg-subtle)" }}>
                    {c.country_code === "SA" ? "🇸🇦" : "🏙️"}
                  </div>
                  <div className="flex-1 min-w-0">
                    {cov.length > 0 ? (
                      <>
                        <div className="font-semibold text-sm truncate">
                          {lang === "ar" ? c.branch_name_ar : c.branch_name_en}
                        </div>
                        <div className="text-[11px] opacity-60 leading-relaxed">
                          {cov.join(" . ")}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-semibold text-sm truncate">{label}</div>
                        {(c.branch_name_ar || c.branch_name_en) && label !== (lang === "ar" ? c.branch_name_ar : c.branch_name_en) && (
                          <div className="text-[11px] opacity-60 truncate">
                            {lang === "ar" ? c.branch_name_ar : c.branch_name_en}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {active && <Check size={18} className="text-emerald-600" />}
                </button>
              );
            })
          )}
        </div>
      </motion.div>
    </div>
  );
}
